import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, addDoc, collection as firestoreCollection } from 'firebase/firestore';
import { db } from '../../services/firebase';

const PartnerCoordinator = () => {
  const [partnerApplications, setPartnerApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editRepresentative, setEditRepresentative] = useState({
    representativeTitleFullNames: '',
    representativePosition: '',
    representativeDate: '',
    representativeSignature: ''
  });
  const [message, setMessage] = useState('');

  useEffect(() => {
    const fetchPartnerApplications = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'partnerApplications'));
        const applications = [];
        querySnapshot.forEach((doc) => {
          applications.push({ id: doc.id, ...doc.data() });
        });
        setPartnerApplications(applications);
        setFilteredApplications(applications);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching partner applications:', error);
        setLoading(false);
      }
    };

    fetchPartnerApplications();
  }, []);

  useEffect(() => {
    let filtered = partnerApplications;
    
    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(app => app.status === statusFilter);
    }
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(app => 
        (app.entityRegisteredName && app.entityRegisteredName.toLowerCase().includes(term)) ||
        (app.tradingName && app.tradingName.toLowerCase().includes(term)) ||
        (app.liaisonTitleFullNames && app.liaisonTitleFullNames.toLowerCase().includes(term)) ||
        (app.businessEmail && app.businessEmail.toLowerCase().includes(term))
      );
    }
    
    setFilteredApplications(filtered);
  }, [statusFilter, searchTerm, partnerApplications]);

  useEffect(() => {
    if (selectedApplication) {
      setEditRepresentative({
        representativeTitleFullNames: selectedApplication.representativeTitleFullNames || '',
        representativePosition: selectedApplication.representativePosition || 'FMLC Business Partner Coordinator',
        representativeDate: selectedApplication.representativeDate || '',
        representativeSignature: selectedApplication.representativeSignature || ''
      });
      setMessage(''); // Reset message input when a new application is selected
    }
  }, [selectedApplication]);

  const updateApplicationStatus = async (applicationId, status) => {
    try {
      const docRef = doc(db, 'partnerApplications', applicationId);
      await updateDoc(docRef, {
        status,
        coordinatorReviewedAt: new Date(),
        coordinatorId: 'current_user_id' // Replace with actual user ID
      });

      setPartnerApplications(prev => 
        prev.map(app => 
          app.id === applicationId ? { ...app, status } : app
        )
      );
      
      alert(`Application status updated to ${status}`);
    } catch (error) {
      console.error('Error updating application status:', error);
      alert('Error updating application status');
    }
  };

  const updateRepresentativeDetails = async () => {
    try {
      const docRef = doc(db, 'partnerApplications', selectedApplication.id);
      await updateDoc(docRef, {
        representativeTitleFullNames: editRepresentative.representativeTitleFullNames,
        representativePosition: editRepresentative.representativePosition,
        representativeDate: editRepresentative.representativeDate,
        representativeSignature: editRepresentative.representativeSignature,
        coordinatorUpdatedAt: new Date(),
        coordinatorId: 'current_user_id' // Replace with actual user ID
      });

      setPartnerApplications(prev =>
        prev.map(app =>
          app.id === selectedApplication.id
            ? { ...app, ...editRepresentative }
            : app
        )
      );
      setSelectedApplication(prev => ({
        ...prev,
        ...editRepresentative
      }));
      
      alert('FMLC Representative details updated successfully');
    } catch (error) {
      console.error('Error updating representative details:', error);
      alert('Error updating representative details');
    }
  };

  const sendMessage = async () => {
    if (!message.trim()) {
      alert('Please enter a message before sending.');
      return;
    }

    try {
      const messagesRef = firestoreCollection(db, 'partnerApplications', selectedApplication.id, 'messages');
      await addDoc(messagesRef, {
        content: message,
        senderId: 'current_user_id', // Replace with actual user ID
        recipientEmail: selectedApplication.businessEmail || 'N/A',
        sentAt: new Date()
      });

      alert('Message sent successfully');
      setMessage(''); // Clear message input after sending
      console.log(`Message sent to ${selectedApplication.businessEmail}: ${message}`);
      // Note: For actual email sending, integrate with Firebase Functions or an email service like SendGrid.
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Error sending message');
    }
  };

  const viewApplicationDetails = (application) => {
    setSelectedApplication(application);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedApplication(null);
    setMessage('');
  };

  const handleRepresentativeChange = (e) => {
    const { name, value } = e.target;
    setEditRepresentative(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleMessageChange = (e) => {
    setMessage(e.target.value);
  };

  const exportToCSV = () => {
    const headers = ['Business Name', 'Contact Person', 'Email', 'Industry', 'Status', 'Submitted Date'];
    const csvData = filteredApplications.map(app => [
      app.entityRegisteredName || app.tradingName || 'N/A',
      app.liaisonTitleFullNames || 'N/A',
      app.businessEmail || 'N/A',
      app.industrySector || 'N/A',
      app.status || 'pending',
      app.submittedAt ? new Date(app.submittedAt.seconds * 1000).toLocaleDateString() : 'N/A'
    ]);
    
    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'partner-applications.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return <div className="loading">Loading partner applications...</div>;
  }

  return (
    <div className="dashboard">
      <h2>Partner Coordinator Dashboard</h2>
      
      <div className="dashboard-controls">
        <div className="filter-control">
          <label>Filter by Status:</label>
          <select 
            value={statusFilter} 
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Applications</option>
            <option value="pending">Pending</option>
            <option value="under_review">Under Review</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
        
        <div className="search-control">
          <input
            type="text"
            placeholder="Search by business name, contact, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <button onClick={exportToCSV} className="btn-primary">
          Export to CSV
        </button>
      </div>

      <div className="dashboard-section">
        <h3>Partner Applications ({filteredApplications.length})</h3>
        <div className="table-container">
          <table className="dashboard-table">
            <thead>
              <tr>
                <th>Business Name</th>
                <th>Contact Person</th>
                <th>Email</th>
                <th>Industry</th>
                <th>Submitted Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredApplications.map(application => (
                <tr key={application.id}>
                  <td>{application.entityRegisteredName || application.tradingName || 'N/A'}</td>
                  <td>{application.liaisonTitleFullNames || 'N/A'}</td>
                  <td>{application.businessEmail || 'N/A'}</td>
                  <td>{application.industrySector || 'N/A'}</td>
                  <td>{application.submittedAt ? new Date(application.submittedAt.seconds * 1000).toLocaleDateString() : 'N/A'}</td>
                  <td>
                    <span className={`status-badge status-${application.status || 'pending'}`}>
                      {application.status || 'Pending'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="btn-primary"
                        onClick={() => viewApplicationDetails(application)}
                      >
                        View
                      </button>
                      <select 
                        value={application.status || 'pending'} 
                        onChange={(e) => updateApplicationStatus(application.id, e.target.value)}
                        className="status-select"
                      >
                        <option value="pending">Pending</option>
                        <option value="under_review">Under Review</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredApplications.length === 0 && (
            <p className="no-data">No partner applications found.</p>
          )}
        </div>
      </div>

      {/* Application Detail Modal */}
      {showModal && selectedApplication && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Partner Application Details</h3>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="application-details">
                <h4>Application Type</h4>
                <div className="detail-row">
                  <span className="detail-label">New Application:</span>
                  <span className="detail-value">{selectedApplication.newApplication ? 'Yes' : 'No'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Renewal Application:</span>
                  <span className="detail-value">{selectedApplication.renewalApplication ? 'Yes' : 'No'}</span>
                </div>

                <h4>Section 1: Entity Information</h4>
                <div className="detail-row">
                  <span className="detail-label">Registered Name:</span>
                  <span className="detail-value">{selectedApplication.entityRegisteredName || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Trading Name:</span>
                  <span className="detail-value">{selectedApplication.tradingName || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Business Registration Number:</span>
                  <span className="detail-value">{selectedApplication.businessRegistrationNumber || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Tax Identification Number:</span>
                  <span className="detail-value">{selectedApplication.taxIdentificationNumber || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Director Names:</span>
                  <span className="detail-value">{selectedApplication.directorNames || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Industry/Sector:</span>
                  <span className="detail-value">{selectedApplication.industrySector || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Office Contact Number:</span>
                  <span className="detail-value">{selectedApplication.officeContactNumber || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Business Email:</span>
                  <span className="detail-value">{selectedApplication.businessEmail || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Physical Business Location:</span>
                  <span className="detail-value">{selectedApplication.physicalBusinessLocation || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Street Address:</span>
                  <span className="detail-value">{selectedApplication.streetAddress || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">District:</span>
                  <span className="detail-value">{selectedApplication.district || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Number of Employees:</span>
                  <span className="detail-value">{selectedApplication.numberOfEmployees || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Website:</span>
                  <span className="detail-value">{selectedApplication.website || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Social Media Links:</span>
                  <span className="detail-value">{selectedApplication.socialMediaLinks || 'N/A'}</span>
                </div>

                <h4>Section 2: Business Liaison Contact</h4>
                <div className="detail-row">
                  <span className="detail-label">Title & Full Names:</span>
                  <span className="detail-value">{selectedApplication.liaisonTitleFullNames || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Job Position:</span>
                  <span className="detail-value">{selectedApplication.liaisonJobPosition || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Department:</span>
                  <span className="detail-value">{selectedApplication.liaisonDepartment || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Contact Number:</span>
                  <span className="detail-value">{selectedApplication.liaisonContactNumber || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Direct Email:</span>
                  <span className="detail-value">{selectedApplication.liaisonDirectEmail || 'N/A'}</span>
                </div>

                <h4>Section 3: Business Line of Operation Survey</h4>
                <div className="detail-row">
                  <span className="detail-label">Selected Operations:</span>
                  <span className="detail-value">
                    {[
                      selectedApplication.groceriesHouseholdEssentials && 'Groceries & Household Essentials',
                      selectedApplication.healthcareWellness && 'Healthcare & Wellness',
                      selectedApplication.clothingPersonalCare && 'Clothing & Personal Care',
                      selectedApplication.businessDevelopmentEntrepreneurship && 'Business Development & Entrepreneurship',
                      selectedApplication.entertainmentLiveEvents && 'Entertainment & Live Events',
                      selectedApplication.housingUtilities && 'Housing & Utilities',
                      selectedApplication.sportsRecreation && 'Sports & Recreation',
                      selectedApplication.educationSkillsDevelopment && 'Education & Skills Development',
                      selectedApplication.tourismArtsCulture && 'Tourism, Arts & Culture',
                      selectedApplication.technologyConnectivity && 'Technology & Connectivity',
                      selectedApplication.travelMobility && 'Travel & Mobility',
                      selectedApplication.lodgingAccommodation && 'Lodging and Accommodation',
                      selectedApplication.industryFacilityTours && 'Industry and Facility Tours',
                      selectedApplication.othersOperation && 'Others'
                    ].filter(Boolean).join(', ') || 'N/A'}
                  </span>
                </div>

                <h4>Section 4: Business Engagement Areas</h4>
                <div className="detail-row">
                  <span className="detail-label">Selected Areas:</span>
                  <span className="detail-value">
                    {[
                      selectedApplication.generalManagement && 'General Management',
                      selectedApplication.operations && 'Operations',
                      selectedApplication.finance && 'Finance',
                      selectedApplication.purchasing && 'Purchasing',
                      selectedApplication.marketing && 'Marketing',
                      selectedApplication.informationTechnology && 'Information Technology',
                      selectedApplication.humanResources && 'Human Resources',
                      selectedApplication.customerService && 'Customer Service',
                      selectedApplication.otherEngagement && `Other: ${selectedApplication.otherEngagement}`
                    ].filter(Boolean).join(', ') || 'N/A'}
                  </span>
                </div>

                <h4>Section 5: Value-Added Offerings</h4>
                <div className="detail-row">
                  <span className="detail-label">Selected Offerings:</span>
                  <span className="detail-value">
                    {[
                      selectedApplication.productServicePromotions && 'Product & Service Promotions',
                      selectedApplication.outdoorAdventures && 'Outdoor Adventures',
                      selectedApplication.industryFacilityToursValue && 'Industry and Facility Tours',
                      selectedApplication.businessDevelopmentEntrepreneurshipValue && 'Business Development & Entrepreneurship',
                      selectedApplication.entertainmentLiveEventsValue && 'Entertainment & Live Events',
                      selectedApplication.communityEngagementCSR && 'Community Engagement & CSR Initiatives',
                      selectedApplication.sportsRecreationValue && 'Sports & Recreation',
                      selectedApplication.familyRelationshipsCounselling && 'Family, Relationships & Counselling',
                      selectedApplication.tourismArtsCultureValue && 'Tourism, Arts & Culture',
                      selectedApplication.wellnessHealthRelaxation && 'Wellness, Health & Relaxation',
                      selectedApplication.travelAccommodation && 'Travel & Accommodation',
                      selectedApplication.wealthFinancialPlanning && 'Wealth & Financial Planning',
                      selectedApplication.otherValueAdded && `Other: ${selectedApplication.otherValueAdded}`
                    ].filter(Boolean).join(', ') || 'N/A'}
                  </span>
                </div>

                <h4>Section 6: Entity Profile & Engagement</h4>
                <div className="detail-row">
                  <span className="detail-label">Years of Operation:</span>
                  <span className="detail-value">{selectedApplication.yearsOfOperation || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Target Market:</span>
                  <span className="detail-value">{selectedApplication.targetMarket || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Age Demographics:</span>
                  <span className="detail-value">{selectedApplication.ageDemographics?.join(', ') || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Effective Marketing Channels:</span>
                  <span className="detail-value">{selectedApplication.effectiveMarketingChannels?.join(', ') || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Collaboration Frequency:</span>
                  <span className="detail-value">{selectedApplication.collaborationFrequency || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Co-host/Sponsor/Mentor:</span>
                  <span className="detail-value">{selectedApplication.coHostSponsorMentor || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Specify How:</span>
                  <span className="detail-value">{selectedApplication.specifyHow?.join(', ') || 'N/A'}</span>
                </div>

                <h4>Section 7: Supplier Empowerment & Business Support</h4>
                <div className="detail-row">
                  <span className="detail-label">Selected Support:</span>
                  <span className="detail-value">
                    {[
                      selectedApplication.clientCustomerSourcing && 'Client, customer sourcing',
                      selectedApplication.marketOpportunitiesSupport && 'Market opportunities and support',
                      selectedApplication.betterPricesDealSuppliers && 'Better prices deal from suppliers',
                      selectedApplication.accessPreferredVendors && 'Access to preferred vendors',
                      selectedApplication.strategicSourcingSupport && 'Strategic sourcing support',
                      selectedApplication.representationSupplierNegotiations && 'Representation during supplier negotiations',
                      selectedApplication.flexibleSupplierPaymentTerms && 'Flexible supplier payment terms',
                      selectedApplication.jointSourcingEfforts && 'Joint sourcing efforts with FMLC partners',
                      selectedApplication.businessDevelopmentServices && 'Business development services',
                      selectedApplication.enterpriseSupportServices && 'Enterprise support services provision',
                      selectedApplication.otherSupplierEmpowerment && `Other: ${selectedApplication.otherSupplierEmpowerment}`
                    ].filter(Boolean).join(', ') || 'N/A'}
                  </span>
                </div>

                <h4>Section 8: Administration Fee & Payment Details</h4>
                <div className="detail-row">
                  <span className="detail-label">Agree to Admin Fee:</span>
                  <span className="detail-value">{selectedApplication.agreeAdminFee ? 'Yes' : 'No'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Payment Method:</span>
                  <span className="detail-value">{selectedApplication.paymentMethod || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">M-Pesa Number:</span>
                  <span className="detail-value">{selectedApplication.mPesaNumber || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">EcoCash Number:</span>
                  <span className="detail-value">{selectedApplication.ecoCashNumber || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Account Holder:</span>
                  <span className="detail-value">{selectedApplication.accountHolder || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Bank Name:</span>
                  <span className="detail-value">{selectedApplication.bankName || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Account No.:</span>
                  <span className="detail-value">{selectedApplication.accountNo || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Branch Code:</span>
                  <span className="detail-value">{selectedApplication.branchCode || 'N/A'}</span>
                </div>

                <h4>Section 9: Agreement & Signature</h4>
                <div className="detail-row">
                  <span className="detail-label">Authorized Signature:</span>
                  <span className="detail-value">{selectedApplication.authorizedSignature || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Date:</span>
                  <span className="detail-value">{selectedApplication.date || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Attachments:</span>
                  <span className="detail-value">
                    {(() => {
                      const links = [
                        selectedApplication.personalIdUrl && <a key="personalId" href={selectedApplication.personalIdUrl} target="_blank" rel="noopener noreferrer">Personal ID</a>,
                        selectedApplication.businessIdUrl && <a key="businessId" href={selectedApplication.businessIdUrl} target="_blank" rel="noopener noreferrer">Business ID</a>,
                        selectedApplication.businessTradersLicenseUrl && <a key="tradersLicense" href={selectedApplication.businessTradersLicenseUrl} target="_blank" rel="noopener noreferrer">Business Trader’s License</a>,
                        selectedApplication.taxClearanceCertificateUrl && <a key="taxClearance" href={selectedApplication.taxClearanceCertificateUrl} target="_blank" rel="noopener noreferrer">Tax Clearance Certificate</a>,
                        selectedApplication.businessUtilityBillUrl && <a key="utilityBill" href={selectedApplication.businessUtilityBillUrl} target="_blank" rel="noopener noreferrer">Business Utility Bill</a>,
                        selectedApplication.businessSubleaseAgreementUrl && <a key="sublease" href={selectedApplication.businessSubleaseAgreementUrl} target="_blank" rel="noopener noreferrer">Business Sublease Agreement</a>
                      ].filter(Boolean);
                      return links.length > 0 ? links.map((link, index) => (
                        <span key={link.key}>{link}{index < links.length - 1 ? ', ' : ''}</span>
                      )) : 'N/A';
                    })()}
                  </span>
                </div>

                <h4>Section 10: Business Partner Application Declaration</h4>
                <div className="detail-row">
                  <span className="detail-label">Applicant Title & Full Names:</span>
                  <span className="detail-value">{selectedApplication.applicantTitleFullNames || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Applicant Position:</span>
                  <span className="detail-value">{selectedApplication.applicantPosition || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Business Name:</span>
                  <span className="detail-value">{selectedApplication.applicantBusinessName || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Applicant Date:</span>
                  <span className="detail-value">{selectedApplication.applicantDate || 'N/A'}</span>
                </div>

                <h4>FMLC Representative (Editable)</h4>
                <div className="detail-row">
                  <span className="detail-label">Title & Full Names:</span>
                  <span className="detail-value">
                    <input
                      type="text"
                      name="representativeTitleFullNames"
                      value={editRepresentative.representativeTitleFullNames}
                      onChange={handleRepresentativeChange}
                    />
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Position:</span>
                  <span className="detail-value">
                    <input
                      type="text"
                      name="representativePosition"
                      value={editRepresentative.representativePosition}
                      onChange={handleRepresentativeChange}
                    />
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Date:</span>
                  <span className="detail-value">
                    <input
                      type="date"
                      name="representativeDate"
                      value={editRepresentative.representativeDate}
                      onChange={handleRepresentativeChange}
                    />
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Signature:</span>
                  <span className="detail-value">
                    <input
                      type="text"
                      name="representativeSignature"
                      value={editRepresentative.representativeSignature}
                      onChange={handleRepresentativeChange}
                    />
                  </span>
                </div>

                <h4>FMLC Approver</h4>
                <div className="detail-row">
                  <span className="detail-label">Title & Full Names:</span>
                  <span className="detail-value">{selectedApplication.approverTitleFullNames || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Position:</span>
                  <span className="detail-value">{selectedApplication.approverPosition || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Date:</span>
                  <span className="detail-value">{selectedApplication.approverDate || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Signature:</span>
                  <span className="detail-value">{selectedApplication.approverSignature || 'N/A'}</span>
                </div>

                <h4>Send Message to Partner</h4>
                <div className="detail-row">
                  <span className="detail-label">Recipient Email:</span>
                  <span className="detail-value">{selectedApplication.businessEmail || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Message:</span>
                  <span className="detail-value">
                    <textarea
                      value={message}
                      onChange={handleMessageChange}
                      placeholder="Enter your query or message..."
                      rows="4"
                      style={{ width: '100%' }}
                    />
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label"></span>
                  <span className="detail-value">
                    <button className="btn-primary" onClick={sendMessage}>
                      Send Message
                    </button>
                  </span>
                </div>

                <h4>Other Comments</h4>
                <div className="detail-row">
                  <span className="detail-label">Comments:</span>
                  <span className="detail-value">{selectedApplication.otherComments || 'N/A'}</span>
                </div>

                <h4>Application Status</h4>
                <div className="detail-row">
                  <span className="detail-label">Current Status:</span>
                  <span className="detail-value">
                    <span className={`status-badge status-${selectedApplication.status || 'pending'}`}>
                      {selectedApplication.status || 'Pending'}
                    </span>
                  </span>
                </div>
                
                <div className="detail-row">
                  <span className="detail-label">Update Status:</span>
                  <span className="detail-value">
                    <select 
                      value={selectedApplication.status || 'pending'} 
                      onChange={(e) => updateApplicationStatus(selectedApplication.id, e.target.value)}
                      className="status-select"
                    >
                      <option value="pending">Pending</option>
                      <option value="under_review">Under Review</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={closeModal}>Close</button>
              <button 
                className="btn-primary"
                onClick={() => {
                  updateRepresentativeDetails();
                  // Optionally close modal after saving
                  // closeModal();
                }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerCoordinator;